import { useEffect, useState, FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { CreditTransaction, Order, Profile } from '@lr/shared';
import { apiFetch } from '../../lib/api';
import { StatusBadge } from './AdminDashboard';

interface Bundle {
  profile: Profile;
  orders: Order[];
  transactions: CreditTransaction[];
}

type Tab = 'orders' | 'credits' | 'profile';

export function AdminCustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<Bundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('orders');
  const [showAdjust, setShowAdjust] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [requestingId, setRequestingId] = useState(false);
  const [idRequestMsg, setIdRequestMsg] = useState<string | null>(null);

  async function requestIdEmail() {
    if (!id) return;
    setRequestingId(true);
    setIdRequestMsg(null);
    try {
      await apiFetch(`/api/admin/users/${id}/request-id`, { method: 'POST' });
      setIdRequestMsg('ID request email sent.');
    } catch (e) {
      setIdRequestMsg(e instanceof Error ? e.message : 'Email failed');
    } finally {
      setRequestingId(false);
    }
  }

  async function deleteUser() {
    if (!data) return;
    const name =
      [data.profile.first_name, data.profile.last_name].filter(Boolean).join(' ') ||
      data.profile.email;
    const typed = window.prompt(
      `This permanently deletes ${name} AND every order, basket, fit return, and credit transaction tied to them. This cannot be undone.\n\nType DELETE to confirm.`
    );
    if (typed !== 'DELETE') return;
    setDeleting(true);
    setError(null);
    try {
      await apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
      navigate('/admin/customers');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
      setDeleting(false);
    }
  }

  function load() {
    setLoading(true);
    apiFetch<Bundle>(`/api/admin/users/${id}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed'))
      .finally(() => setLoading(false));
  }
  useEffect(load, [id]);

  if (loading) return <div style={{ color: 'var(--a-ink-soft)', padding: 16 }}>Loading…</div>;
  if (error || !data) return <div className="a-alert a-alert-error">{error ?? 'Not found'}</div>;

  const p = data.profile;
  const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ') || '—';
  const rentalOrders = data.orders.filter((o) => o.bundle_purchased_id == null);
  const purchaseOrders = data.orders.filter((o) => o.bundle_purchased_id != null);

  return (
    <>
      <div className="admin-breadcrumb">
        <Link to="/admin/customers">Customers</Link>
        <span className="sep">/</span>
        <span>{fullName}</span>
      </div>
      <div className="admin-page-head">
        <div>
          <h1>{fullName}</h1>
          <div className="sub">{p.email}</div>
        </div>
        <div className="actions" style={{ flexWrap: 'wrap' }}>
          {p.id_status !== 'approved' && (
            <button
              type="button"
              className="btn-secondary"
              disabled={requestingId}
              onClick={() => void requestIdEmail()}
            >
              {requestingId ? 'Sending…' : 'Email request ID upload'}
            </button>
          )}
          <button className="btn-secondary" onClick={() => setShowAdjust(true)}>
            Adjust credits
          </button>
          {p.role === 'user' && (
            <button
              type="button"
              className="btn-ghost"
              disabled={deleting}
              onClick={() => void deleteUser()}
              style={{ color: 'var(--a-danger)' }}
            >
              {deleting ? 'Deleting…' : 'Delete customer'}
            </button>
          )}
        </div>
      </div>
      {idRequestMsg && (
        <div
          className={`a-alert ${
            idRequestMsg.includes('sent') ? 'a-alert-good' : 'a-alert-error'
          }`}
        >
          {idRequestMsg}
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi">
          <div className="lbl">Credits</div>
          <div className="val">{p.credit_balance}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Rental orders</div>
          <div className="val">{rentalOrders.length}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Credit purchases</div>
          <div className="val">{purchaseOrders.length}</div>
        </div>
        <div className="kpi">
          <div className="lbl">Joined</div>
          <div className="val" style={{ fontSize: 16 }}>
            {new Date(p.created_at).toLocaleDateString('en-GB')}
          </div>
        </div>
      </div>

      <div className="a-tabs">
        <button className={`a-tab${tab === 'orders' ? ' active' : ''}`} onClick={() => setTab('orders')}>
          Orders
        </button>
        <button className={`a-tab${tab === 'credits' ? ' active' : ''}`} onClick={() => setTab('credits')}>
          Credit history
        </button>
        <button className={`a-tab${tab === 'profile' ? ' active' : ''}`} onClick={() => setTab('profile')}>
          Profile
        </button>
      </div>

      {tab === 'orders' && (
        <div className="a-card">
          <div className="a-card-head">
            <h2>All orders ({data.orders.length})</h2>
          </div>
          {data.orders.length === 0 ? (
            <div className="a-empty">
              <div className="h">No orders yet</div>
              <div className="p">This customer hasn&apos;t checked out yet.</div>
            </div>
          ) : (
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th style={{ width: 100 }}>Credits</th>
                    <th style={{ width: 120 }}>Amount</th>
                    <th style={{ width: 130 }}>Placed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.id}>
                      <td className="id">{o.order_number}</td>
                      <td>
                        {o.bundle_purchased_id ? (
                          <span className="badge badge-info">Credit purchase</span>
                        ) : (
                          <span className="badge badge-neutral">Rental</span>
                        )}
                      </td>
                      <td>
                        <StatusBadge status={o.status} />
                      </td>
                      <td className="num">{o.credits_used}</td>
                      <td className="num">£{(o.total_paid_pence / 100).toFixed(2)}</td>
                      <td>{new Date(o.created_at).toLocaleDateString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'credits' && (
        <div className="a-card">
          <div className="a-card-head">
            <h2>Credit transactions ({data.transactions.length})</h2>
          </div>
          {data.transactions.length === 0 ? (
            <div className="a-empty">
              <div className="h">No transactions</div>
              <div className="p">Credit additions/spends appear here.</div>
            </div>
          ) : (
            <div className="a-table-wrap">
              <table className="a-table">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>Date</th>
                    <th>Reason</th>
                    <th style={{ width: 100 }}>Amount</th>
                    <th style={{ width: 100 }}>Balance after</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t) => (
                    <tr key={t.id}>
                      <td>{new Date(t.created_at).toLocaleDateString('en-GB')}</td>
                      <td>
                        <span className="badge badge-neutral">{t.reason.replace(/_/g, ' ')}</span>
                      </td>
                      <td
                        className="num"
                        style={{
                          color: t.amount >= 0 ? 'var(--a-good)' : 'var(--a-danger)',
                          fontWeight: 600
                        }}
                      >
                        {t.amount > 0 ? '+' : ''}
                        {t.amount}
                      </td>
                      <td className="num">{t.balance_after}</td>
                      <td style={{ fontSize: 12, color: 'var(--a-ink-soft)' }}>
                        {t.notes ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'profile' && (
        <div className="a-card">
          <div className="a-card-head">
            <h2>Profile</h2>
          </div>
          <div className="a-card-body">
            <dl className="a-dl">
              <dt>Full name</dt>
              <dd>{fullName}</dd>
              <dt>Email</dt>
              <dd>{p.email}</dd>
              <dt>Phone</dt>
              <dd>{p.phone ?? '—'}</dd>
              <dt>Role</dt>
              <dd>
                <span className="badge badge-neutral">{p.role}</span>
              </dd>
              <dt>Marketing opt-in</dt>
              <dd>{p.marketing_opt_in ? 'Yes' : 'No'}</dd>
              <dt>Address</dt>
              <dd>
                {[
                  p.shipping_line1,
                  p.shipping_line2,
                  p.shipping_city,
                  p.shipping_postcode,
                  p.shipping_country
                ]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </dd>
              <dt>Stripe customer</dt>
              <dd className="id">{p.stripe_customer_id ?? '—'}</dd>
            </dl>
          </div>
        </div>
      )}

      {showAdjust && (
        <AdjustCreditsModal
          userId={p.id}
          balance={p.credit_balance}
          onClose={(adjusted) => {
            setShowAdjust(false);
            if (adjusted) load();
          }}
        />
      )}
    </>
  );
}

function AdjustCreditsModal({
  userId,
  balance,
  onClose
}: {
  userId: string;
  balance: number;
  onClose: (adjusted?: boolean) => void;
}) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [kind, setKind] = useState<'admin_adjustment' | 'fit_return_refund'>(
    'admin_adjustment'
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFitReturn = kind === 'fit_return_refund';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const n = Number(amount);
      if (!Number.isInteger(n) || n === 0)
        throw new Error('Amount must be a non-zero integer.');
      if (isFitReturn && n <= 0)
        throw new Error('Fit return refunds must add credits (positive amount).');
      if (!notes.trim())
        throw new Error('Please add a note explaining the adjustment.');
      await apiFetch('/api/admin/credits/adjust', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, amount: n, notes, kind })
      });
      onClose(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="a-modal-overlay open" onClick={() => onClose()} />
      <aside className="a-modal open">
        <form onSubmit={onSubmit} style={{ display: 'contents' }}>
          <div className="a-modal-head">
            <h2>Adjust credits</h2>
            <button type="button" className="btn-ghost" onClick={() => onClose()}>
              ×
            </button>
          </div>
          <div className="a-modal-body">
            {error && <div className="a-alert a-alert-error">{error}</div>}
            <div className="a-alert a-alert-info">
              Current balance:{' '}
              <strong>
                {balance} {balance === 1 ? 'credit' : 'credits'}
              </strong>
              .{' '}
              {isFitReturn
                ? 'Fit return refunds add credits and grant a delivery-fee exemption on the customer’s next sub-3 basket.'
                : 'Use a negative number to remove credits.'}
            </div>
            <div className="a-form">
              <div className="a-field">
                <label>Reason type *</label>
                <select
                  value={kind}
                  onChange={(e) =>
                    setKind(
                      e.target.value as 'admin_adjustment' | 'fit_return_refund'
                    )
                  }
                >
                  <option value="admin_adjustment">
                    Goodwill / general adjustment
                  </option>
                  <option value="fit_return_refund">FIT RETURN refund</option>
                </select>
                <div className="help">
                  {isFitReturn
                    ? 'Use for items returned because they didn’t fit. The customer is granted a one-off £4.99 delivery exemption so a 1- or 2-credit basket can ship.'
                    : 'Use for goodwill credits, mistakes, promotions, etc.'}
                </div>
              </div>
              <div className="a-field">
                <label>Amount *</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={isFitReturn ? 'e.g. 1' : 'e.g. 3 or -1'}
                  min={isFitReturn ? 1 : undefined}
                  required
                />
              </div>
              <div className="a-field">
                <label>Note *</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    isFitReturn
                      ? 'e.g. Customer returned the navy blazer — wrong fit.'
                      : 'Explain why — the customer will see this in their email.'
                  }
                  rows={3}
                  required
                />
                <div className="help">
                  The customer is emailed automatically with this note.
                </div>
              </div>
            </div>
          </div>
          <div className="a-modal-foot">
            <button type="button" className="btn-secondary" onClick={() => onClose()}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving
                ? 'Adjusting…'
                : isFitReturn
                  ? 'Refund as fit return'
                  : 'Adjust credits'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}
